import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Trophy, Medal, TrendingUp, Loader2, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PvpResult() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [opponent, setOpponent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isWinner, setIsWinner] = useState(false);

  useEffect(() => {
    loadResult();
  }, [matchId]);

  const loadResult = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/pvp/match/${matchId}`, {
        credentials: 'include'
      });
      const data = await response.json();
      setMatch(data.match);
      setOpponent(data.opponent);
      
      // Get current user
      const userResponse = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include'
      });
      const userData = await userResponse.json();
      setIsWinner(data.match.winner_id === userData.user_id);
      
      setLoading(false);
    } catch (error) {
      toast.error("Помилка завантаження результату");
      navigate('/pvp');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-primary animate-spin" />
      </div>
    );
  }

  const playerScore = match.scores?.player1 || 0;
  const opponentScore = match.scores?.player2 || 0;
  const isDraw = playerScore === opponentScore;

  return (
    <div className="min-h-screen bg-background">
      <div className="grid-background min-h-screen">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="space-y-8"
          >
            <div className="text-center">
              {isDraw ? (
                <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-muted mb-6">
                  <Medal className="w-12 h-12 text-muted-foreground" />
                </div>
              ) : isWinner ? (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1, rotate: 360 }}
                  transition={{ type: "spring", duration: 1 }}
                  className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-yellow-500 to-orange-500 mb-6"
                >
                  <Trophy className="w-12 h-12 text-white" />
                </motion.div>
              ) : (
                <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-muted/50 mb-6">
                  <TrendingUp className="w-12 h-12 text-muted-foreground" />
                </div>
              )}

              <h1 className="text-5xl md:text-6xl font-black text-foreground mb-4">
                {isDraw ? "Нічия!" : isWinner ? "Перемога!" : "Поразка"}
              </h1>
              <p className="text-lg text-muted-foreground">
                {isDraw 
                  ? "Обидва рішення однаково сильні!" 
                  : isWinner 
                    ? "Ви довели свою майстерність!" 
                    : "Відмінна спроба! Аналізуйте помилки і повертайтесь сильнішими!"}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-card/50 backdrop-blur-sm border border-white/5 p-6 rounded-xl text-center"
              >
                <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-2xl text-white font-bold">Ви</span>
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Ваш Скор</h3>
                <div className="text-5xl font-black text-primary mb-2">{playerScore}</div>
                <div className="text-sm text-muted-foreground">/ 100</div>
              </motion.div>

              <div className="flex items-center justify-center">
                <div className="text-4xl font-bold text-muted-foreground">VS</div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-card/50 backdrop-blur-sm border border-white/5 p-6 rounded-xl text-center"
              >
                <Avatar className="w-16 h-16 mx-auto mb-3 border-2 border-secondary">
                  <AvatarImage src={opponent?.picture} />
                  <AvatarFallback>{opponent?.name?.charAt(0)}</AvatarFallback>
                </Avatar>
                <h3 className="text-lg font-bold text-foreground mb-2">{opponent?.name}</h3>
                <div className="text-5xl font-black text-secondary mb-2">{opponentScore}</div>
                <div className="text-sm text-muted-foreground">/ 100</div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-card/50 backdrop-blur-sm border border-white/5 p-8 rounded-2xl"
            >
              <h3 className="text-2xl font-bold text-foreground mb-4 flex items-center">
                <Trophy className="w-6 h-6 text-primary mr-2" />
                AI Аналіз Рішень
              </h3>
              <div className="prose prose-invert max-w-none">
                <p className="text-foreground leading-relaxed whitespace-pre-wrap">
                  {match.ai_analysis}
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-card/50 backdrop-blur-sm border border-white/5 p-6 rounded-xl"
            >
              <h3 className="text-xl font-bold text-foreground mb-4">Ваше Рішення</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {match.player1_solution && Object.entries(match.player1_solution).map(([key, value]) => (
                  <div key={key} className="bg-muted/30 p-3 rounded-lg">
                    <p className="text-xs text-muted-foreground uppercase mb-1">
                      {key.replace('_', ' ')}
                    </p>
                    <p className="text-sm font-bold text-foreground">{value}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/pvp')}
                data-testid="play-again-button"
                size="lg"
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white h-14 px-10 text-lg rounded-xl font-bold"
              >
                Грати Ще Раз
              </Button>
              <Button
                onClick={() => navigate('/pvp/leaderboard')}
                variant="outline"
                size="lg"
                className="h-14 px-10 text-lg"
              >
                Таблиця Лідерів
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="ghost"
                size="lg"
                className="h-14 px-10 text-lg"
              >
                <Home className="w-5 h-5 mr-2" />
                На Головну
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
